/** Role helpers over the Cognito ID token. */
import type { User } from "oidc-client-ts";

/** Cognito groups, mirroring the workpaper Cover Sheet sign-off rows.
 *  Defined here rather than in config so that importing auth helpers does not
 *  drag in runtime configuration validation. */
export const ROLES = {
  admin: "Admin",
  preparer: "Preparer",
  reviewer: "Reviewer",
  director: "DirectorApproval",
} as const;

/**
 * Groups from the ID token.
 *
 * Cognito emits `cognito:groups` as a JSON array in the token itself; the
 * bracketed-string shapes API Gateway produces do not occur here, but the
 * array is still defensively normalised because an absent claim is normal for
 * a user an admin has not assigned a role to yet.
 */
export function groupsOf(user: User | null | undefined): string[] {
  const raw = user?.profile?.["cognito:groups"];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === "string" && raw.length > 0) return [raw];
  return [];
}

export function hasRole(user: User | null | undefined, role: string): boolean {
  return groupsOf(user).includes(role);
}

export function isAdmin(user: User | null | undefined): boolean {
  return hasRole(user, ROLES.admin);
}

/** A user with no group cannot do anything, and should be told why. */
export function hasAnyRole(user: User | null | undefined): boolean {
  return groupsOf(user).length > 0;
}

export function displayName(user: User | null | undefined): string {
  const profile = user?.profile;
  return String(profile?.email ?? profile?.["cognito:username"] ?? "Signed in");
}
